import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@/lib/api";

interface ResourceState<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

/**
 * Fetch-on-deps-change helper for the transcript pages: runs `fetcher` whenever
 * `deps` change, tracks loading/error, discards stale responses when deps change
 * mid-flight, and exposes a manual `reload`. Kept feature-local — the transcript
 * pages all share this shape (client + T/S + a refresh counter).
 */
export function useResource<T>(fetcher: () => Promise<T>, deps: unknown[]): ResourceState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    fetcher()
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof ApiError ? err.message : "Could not load data.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // `fetcher` is intentionally excluded — callers pass a fresh closure each
    // render; `deps` + `nonce` are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload };
}
